import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
import { AnchoredList, useAnchor } from './anchored-list.js';

const SCAN_SPEED_MS = 34;

const DEBOUNCE_MS = 130;

const SCAN_SETTLE_MS = 45;

const MIN_QUERY = 2;

export interface ScanHit {
  sku: string;
  productName: string;
  brand?: string | null;
  baseUom: string;
  saleUom: string;
  scanUom: string;
  packSizeBase: string | null;
  qtyPerScan: string;
  scanQty: string;
  baseFactor: string;
  warehouseCode: string | null;
  balanceQty: string;
  availableQty: string;
  movingAvgRate: string;
  batchNo: string | null;
  expiryDate: string | null;
  batchQty: string | null;
  daysToExpiry: number | null;
  unitRate: string | null;
  salesTaxPct: string | null;
  trackBatch: boolean;
  trackExpiry: boolean;
  matchKind: 'BARCODE' | 'SKU_EXACT' | 'SKU' | 'BATCH' | 'NAME';
}

export interface ScanBarProps {
  lookup: (query: string) => Promise<{ exact: ScanHit | null; hits: ScanHit[] }>;
  onPick: (hit: ScanHit) => void;
  placeholder?: string;
  status?: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  className?: string;
  'aria-label'?: string;
}

export function ScanBar({
  lookup,
  onPick,
  placeholder = 'Scan a barcode, or type a SKU, item, brand or batch',
  status,
  disabled,
  disabledReason,
  className,
  'aria-label': ariaLabel = 'Scan or search for an item',
}: ScanBarProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ScanHit[]>([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [missed, setMissed] = useState<string | null>(null);

  const { ref: inputRef, anchor } = useAnchor(open);
  const listRef = useRef<HTMLDivElement>(null);

  const lastKeyAt = useRef(0);
  const machineTyped = useRef(false);
  const generation = useRef(0);

  const reset = useCallback(() => {
    setQuery('');
    setHits([]);
    setActive(0);
    setOpen(false);
    machineTyped.current = false;
  }, []);

  const take = useCallback(
    (hit: ScanHit) => {
      onPick(hit);
      setMissed(null);
      reset();
      inputRef.current?.focus();
    },
    [onPick, reset, inputRef],
  );

  const resolve = useCallback(
    async (text: string, immediate: boolean) => {
      const term = text.trim();
      if (term.length < (immediate ? 1 : MIN_QUERY)) {
        setHits([]);
        setOpen(false);
        return;
      }
      const mine = ++generation.current;
      setBusy(true);
      try {
        const result = await lookup(term);
        if (mine !== generation.current) return;

        if (result.exact) {
          take(result.exact);
          return;
        }
        setHits(result.hits);
        setActive(0);
        setOpen(result.hits.length > 0);
        if (immediate) setMissed(result.hits.length === 0 ? term : null);
      } catch {
        if (mine === generation.current) {
          setHits([]);
          setOpen(false);
        }
      } finally {
        if (mine === generation.current) setBusy(false);
      }
    },
    [lookup, take],
  );

  useEffect(() => {
    if (query.trim().length < MIN_QUERY) {
      setHits([]);
      setOpen(false);
      return;
    }
    const scanned = machineTyped.current;
    const timer = setTimeout(
      () => void resolve(query, scanned),
      scanned ? SCAN_SETTLE_MS : DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [query, resolve]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    if (event.key.length === 1) {
      machineTyped.current = now - lastKeyAt.current < SCAN_SPEED_MS;
      lastKeyAt.current = now;
    }

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (open && hits[active]) take(hits[active]);
        else void resolve(query, true);
        break;
      case 'ArrowDown':
        if (hits.length === 0) return;
        event.preventDefault();
        setOpen(true);
        setActive((i) => Math.min(i + 1, hits.length - 1));
        break;
      case 'ArrowUp':
        if (hits.length === 0) return;
        event.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case 'Escape':
        if (query === '' && hits.length === 0 && missed === null) return;
        event.preventDefault();
        event.stopPropagation();
        reset();
        setMissed(null);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-noprint="1">
      <div className="relative flex items-center">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 text-[15px] text-[var(--ink-3)]"
        >
          ⌗
        </span>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          value={query}
          placeholder={disabled ? (disabledReason ?? placeholder) : placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            if (missed) setMissed(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            if (listRef.current?.contains(event.relatedTarget as Node)) return;
            setOpen(false);
          }}
          className={cn(
            'h-11 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pr-24 pl-9',
            'text-[14px] text-[var(--ink)] outline-none',
            'placeholder:text-[var(--ink-3)]',
            'transition-[border-color,box-shadow] duration-[var(--dur-fast)]',
            'focus:border-[var(--accent)] focus:bg-[var(--surface)] focus:ring-2 focus:ring-[var(--accent-soft)]',
            'disabled:cursor-not-allowed disabled:text-[var(--ink-3)]',
            missed ? 'border-[var(--warn)]' : 'border-[var(--line)]',
          )}
        />
        <span className="pointer-events-none absolute right-3.5 text-[11px] text-[var(--ink-3)]">
          {busy ? 'looking…' : 'Enter'}
        </span>
      </div>

      {missed && (
        <p className="m-0 text-[11.5px] text-[var(--warn)]">
          Nothing matches <span className="bb-mono">{missed}</span> — no barcode, SKU, batch or item
          name. Check the warehouse, or add the label on Product Management.
        </p>
      )}
      {!missed && status && <p className="m-0 text-[11.5px] text-[var(--ink-3)]">{status}</p>}

      {open && anchor && (
        <AnchoredList anchor={anchor} id={listId} listRef={listRef} minWidth={360}>
          {hits.map((hit, index) => {
            const empty = Number(hit.availableQty) <= 0;
            const soon = hit.daysToExpiry !== null && hit.daysToExpiry < 30;
            return (
              <div
                key={`${hit.sku} ${hit.batchNo ?? ''} ${index}`}
                role="option"
                data-index={index}
                aria-selected={index === active}
                aria-disabled={empty || undefined}
                onMouseEnter={() => setActive(index)}
                onClick={() => !empty && take(hit)}
                className={cn(
                  'flex items-baseline gap-2.5 px-3 py-2 text-[12.5px]',
                  empty ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
                  index === active && !empty && 'bg-[var(--accent-soft)]',
                )}
              >
                <span className="bb-mono shrink-0 text-[12px] text-[var(--accent)]">{hit.sku}</span>
                <span className="min-w-0 flex-1 truncate text-[var(--ink)]">
                  {hit.productName}
                  {hit.batchNo && (
                    <span
                      className={cn(
                        'bb-mono pl-2 text-[11px]',
                        soon ? 'text-[var(--warn)]' : 'text-[var(--ink-3)]',
                      )}
                    >
                      {hit.batchNo}
                      {hit.expiryDate ? ` · exp ${hit.expiryDate}` : ''}
                      {soon ? ` · ${hit.daysToExpiry}d` : ''}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'bb-num shrink-0 text-[11.5px] tabular-nums',
                    empty ? 'text-[var(--debit)]' : 'text-[var(--ink-3)]',
                  )}
                >
                  {empty ? 'none' : `${Number(hit.availableQty).toLocaleString()} ${hit.baseUom}`}
                </span>
              </div>
            );
          })}
        </AnchoredList>
      )}
    </div>
  );
}
